import {Injectable, InjectionToken} from '@angular/core';
import {Store} from '@ngrx/store';
import {NgbDate, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';

import {DatePickerUtil} from '../date-picker.util';
import {DayOfWeek} from '../../../../common/constants';
import {CalculateUkWeekEndingDate, CalculateUsaWeekEndingDate} from '../../../../common/utilities/timecard';
import {IServerDateService} from '../../../../common/date-service.model';
import {getState} from '../utils';
import {DateFormats, DateFormatType} from '../../models/region.model';
import {selectProjectRegionTimeFormats} from '../../selectors/region.selector';

/**
 * Client Date Service will be responsible for handling all the stuff related to dates in the client side.
 * In App Module, we are providing ClientDateServiceToken with either ClientDateUKService or ClientDateUSAService class based on the project region in store
 * with the help of useFactory function.
 * So we will be able to inject this service anywhere in the app.
 * ClientDateUKService and ClientDateUSAService will have different implementations of IClientDateService interface based on their region specs.
 */

interface IClientDateCommonService {
  getDateStringFromNgbDate: (date: NgbDate, format: string) => string;
  getNgbDateStructFromDateString: (dateString: string, format: string) => NgbDateStruct;
  getNgbDateStructFromDate: (date: Date) => NgbDateStruct;
  getYesterdayDate: () => Date;
}

export interface IClientDateService extends IServerDateService, IClientDateCommonService {
  getLastWeekEndingNgbDate: () => NgbDateStruct;
  getLastWeekEndingString: (format: string) => string;
  isWeekEnding: (date: NgbDateStruct) => boolean;
}

export const ClientDateServiceToken = new InjectionToken<IClientDateService>('ClientDateService');
export const NgbDateFormatToken = new InjectionToken<string>('NgbDateFormat');

export function ngbDateFormatFactory(store: Store, dateFormatType: DateFormatType) {
  return getState<DateFormats>(store, selectProjectRegionTimeFormats)[dateFormatType];
}

class ClientDateCommonService implements IClientDateCommonService {
  getDateStringFromNgbDate(date: NgbDate, format: string): string {
    return DatePickerUtil.getDateStringFromNgbDate(date, format);
  }

  getNgbDateStructFromDate(date: Date): NgbDateStruct {
    return DatePickerUtil.getNgbDateStructFromDate(date);
  }

  getNgbDateStructFromDateString(dateString: string, format: string): NgbDateStruct {
    return DatePickerUtil.getNgbDateStructFromDateString(dateString, format);
  }

  getYesterdayDate(): Date {
    return DatePickerUtil.getYesterdayDate();
  }
}

@Injectable()
export class ClientDateUKService extends ClientDateCommonService implements IClientDateService {
  private _weekStartsOn = DayOfWeek.Monday;

  get weekStartsOn() {
    return this._weekStartsOn;
  }

  getWeekEndingDate(shiftDate: string, format: string): string {
    return CalculateUkWeekEndingDate(shiftDate, format);
  }

  getLastWeekEndingNgbDate(): NgbDateStruct {
    return DatePickerUtil.getLastSundayNgbDate();
  }

  getLastWeekEndingString(format: string): string {
    return DatePickerUtil.getLastSundayString(format);
  }

  isWeekEnding(date: NgbDateStruct): boolean {
    return DatePickerUtil.isSunday(date);
  }
}

@Injectable()
export class ClientDateUSAService extends ClientDateCommonService implements IClientDateService {
  private _weekStartsOn = DayOfWeek.Sunday;

  get weekStartsOn() {
    return this._weekStartsOn;
  }

  getWeekEndingDate(shiftDate: string, format: string): string {
    return CalculateUsaWeekEndingDate(shiftDate, format);
  }

  getLastWeekEndingNgbDate(): NgbDateStruct {
    return DatePickerUtil.getLastSaturdayNgbDate();
  }

  getLastWeekEndingString(format: string): string {
    return DatePickerUtil.getLastSaturdayString(format);
  }

  isWeekEnding(date: NgbDateStruct): boolean {
    return DatePickerUtil.isSaturday(date);
  }
}
